<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  selectedVersion: {
    type: String,
    default: null,
  },
  entitlements: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['select']);

const { t } = useI18n();

const versions = computed(() => [
  {
    id: '1.0',
    name: t('LIA_AGENTS.VERSIONS.LIA_10'),
    subtitle: t('LIA_AGENTS.VERSIONS.LIA_10_SUBTITLE'),
    price: t('LIA_AGENTS.WIZARD.VERSION_INCLUDED'),
    priceDetail: t('LIA_AGENTS.WIZARD.VERSION_PRICE_DETAIL'),
    icon: 'i-lucide-message-circle',
    color: 'slate',
    features: t('LIA_AGENTS.VERSIONS.LIA_10_FEATURES'),
    available: props.entitlements?.can_create_10 === true,
    limit:
      props.entitlements?.versions?.find(v => v.version === '1.0')?.available ||
      0,
    used:
      props.entitlements?.versions?.find(v => v.version === '1.0')?.used || 0,
  },
  {
    id: '1.4',
    name: t('LIA_AGENTS.VERSIONS.LIA_14'),
    subtitle: t('LIA_AGENTS.VERSIONS.LIA_14_SUBTITLE'),
    price: 'R$ 97',
    priceDetail: t('LIA_AGENTS.WIZARD.VERSION_MONTH'),
    icon: 'i-lucide-sparkles',
    color: 'blue',
    features: t('LIA_AGENTS.VERSIONS.LIA_14_FEATURES'),
    available: props.entitlements?.can_create_14 === true,
    limit:
      props.entitlements?.versions?.find(v => v.version === '1.4')?.available ||
      0,
    used:
      props.entitlements?.versions?.find(v => v.version === '1.4')?.used || 0,
  },
  {
    id: '2.1',
    name: t('LIA_AGENTS.VERSIONS.LIA_21'),
    subtitle: t('LIA_AGENTS.VERSIONS.LIA_21_SUBTITLE'),
    price: 'R$ 697',
    priceDetail: t('LIA_AGENTS.WIZARD.VERSION_MONTH'),
    icon: 'i-lucide-calendar',
    color: 'purple',
    features: t('LIA_AGENTS.VERSIONS.LIA_21_FEATURES'),
    available: props.entitlements?.can_create_21 === true,
    limit:
      props.entitlements?.versions?.find(v => v.version === '2.1')?.limit || 0,
    used:
      props.entitlements?.versions?.find(v => v.version === '2.1')?.used || 0,
  },
  {
    id: '3.1',
    name: t('LIA_AGENTS.VERSIONS.LIA_31'),
    subtitle: t('LIA_AGENTS.VERSIONS.LIA_31_SUBTITLE'),
    price: 'R$ 997',
    priceDetail: t('LIA_AGENTS.WIZARD.VERSION_MONTH'),
    icon: 'i-lucide-wifi',
    color: 'green',
    features: t('LIA_AGENTS.VERSIONS.LIA_31_FEATURES'),
    available: props.entitlements?.can_create_31 === true,
    limit:
      props.entitlements?.versions?.find(v => v.version === '3.1')?.limit || 0,
    used:
      props.entitlements?.versions?.find(v => v.version === '3.1')?.used || 0,
  },
]);

const selectVersion = version => {
  if (version.available) {
    emit('select', version.id);
  }
};

const getColorClasses = (color, isSelected) => {
  const colors = {
    blue: {
      border: isSelected
        ? 'border-blue-500 ring-2 ring-blue-500'
        : 'border-n-weak hover:border-blue-400',
      bg: 'bg-blue-500',
      text: 'text-blue-500',
      light: 'bg-blue-50 dark:bg-blue-900/20',
    },
    purple: {
      border: isSelected
        ? 'border-purple-500 ring-2 ring-purple-500'
        : 'border-n-weak hover:border-purple-400',
      bg: 'bg-purple-500',
      text: 'text-purple-500',
      light: 'bg-purple-50 dark:bg-purple-900/20',
    },
    green: {
      border: isSelected
        ? 'border-green-500 ring-2 ring-green-500'
        : 'border-n-weak hover:border-green-400',
      bg: 'bg-green-500',
      text: 'text-green-500',
      light: 'bg-green-50 dark:bg-green-900/20',
    },
    slate: {
      border: isSelected
        ? 'border-slate-500 ring-2 ring-slate-500'
        : 'border-n-weak hover:border-slate-400',
      bg: 'bg-slate-500',
      text: 'text-slate-500',
      light: 'bg-slate-50 dark:bg-slate-900/20',
    },
  };
  return colors[color] || colors.blue;
};
</script>

<template>
  <div class="space-y-6">
    <div class="text-center">
      <h2 class="text-2xl font-bold text-n-slate-12">
        {{ t('LIA_AGENTS.WIZARD.TITLE_SELECT') }}
      </h2>
      <p class="mt-2 text-n-slate-11">
        {{ t('LIA_AGENTS.WIZARD.SUBTITLE_SELECT') }}
      </p>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div
        v-for="version in versions"
        :key="version.id"
        class="relative rounded-xl border-2 p-6 cursor-pointer transition-all duration-200"
        :class="[
          getColorClasses(version.color, selectedVersion === version.id).border,
          version.available ? '' : 'opacity-50 cursor-not-allowed',
        ]"
        @click="selectVersion(version)"
      >
        <!-- Badge de selecionado -->
        <div
          v-if="selectedVersion === version.id"
          class="absolute -top-3 -right-3 w-6 h-6 rounded-full flex items-center justify-center"
          :class="getColorClasses(version.color, true).bg"
        >
          <span class="i-lucide-check size-4 text-white" />
        </div>

        <!-- Header -->
        <div class="flex items-start justify-between mb-4">
          <div
            class="w-12 h-12 rounded-xl flex items-center justify-center"
            :class="getColorClasses(version.color, false).light"
          >
            <span
              class="size-6"
              :class="[
                version.icon,
                getColorClasses(version.color, false).text,
              ]"
            />
          </div>

          <!-- Limite -->
          <div
            v-if="version.available && version.limit > 0"
            class="text-xs text-n-slate-11"
          >
            {{ version.used }}/{{ version.limit }}
          </div>
        </div>

        <!-- Titulo e Subtitulo -->
        <h3 class="text-xl font-bold text-n-slate-12">
          {{ version.name }}
        </h3>
        <p class="text-sm text-n-slate-11 mb-4">
          {{ version.subtitle }}
        </p>

        <!-- Preco -->
        <div class="mb-4">
          <span class="text-2xl font-bold text-n-slate-12">{{
            version.price
          }}</span>
          <span class="text-sm text-n-slate-11">{{ version.priceDetail }}</span>
        </div>

        <!-- Features -->
        <ul class="space-y-2">
          <li
            v-for="feature in version.features"
            :key="feature"
            class="flex items-start gap-2 text-sm text-n-slate-11"
          >
            <span
              class="i-lucide-check size-4 mt-0.5 flex-shrink-0"
              :class="getColorClasses(version.color, false).text"
            />
            {{ feature }}
          </li>
        </ul>

        <!-- Aviso se nao disponivel -->
        <div
          v-if="!version.available"
          class="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg"
        >
          <p class="text-xs text-amber-700 dark:text-amber-300">
            <span class="i-lucide-lock size-3 inline mr-1" />
            {{ t('LIA_AGENTS.WIZARD.LOCKED_MESSAGE') }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
